/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import {
    Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, 
    MenuItem,
    Select,
    Grid,
    TextField,
} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { Api, ApiKeyGenerationRequest, ApiKeysReducer, ApplicationReducer, ApplicationState, EndpointInfo, EndpointURL, GraphqlReducer, KeyType, SubscriptionPoliciesReducer, SubscriptionsReducer, UserReducer } from "../../../../data/interfaces";
import { useStyles } from "./style";
import { fetchApplicationList, fetchSubscriptionPoliciesList, generateApiKeyV2 } from "../../../../data/operations";
import SubscriptionPolicyCard from "../../../app/AppSubscriptions/SubscriptionPolicyCard";
import { FormattedMessage, useIntl } from "react-intl";
import GreyButton from "../../../../elements/GreyButton";
import PrimaryButton from "../../../../elements/PrimaryButton";
import { PreLoaderLogo } from "../../../../elements/PreLoaderLogo";
import CreateApplicationFormContent from "../../../app/CreateApp/CreateApplicationFormContent";
import ApiKeyView from '../../../shared/ApiKeys/ApiKeyView'
import CustomIcons from "../../../../icons/CustomIcons";
import { Warning } from "@material-ui/icons";
import Notification from '../../../ChoreoSystem/Notification/Notification';
import { ApplicationPermission } from "../../../../util/app-permissions";
import { AppPermission } from "../../../../data/emus";
import CustomAutoComplete from "../../../../elements/CustomAutoComplete/CustomAutoComplete";
import Settings from "../../../../conf/settings";

interface ApiKeyGenerationWizardProps {
    open: boolean,
    apiInfo: Api,

    applicationReducer: ApplicationReducer,
    userReducer: UserReducer,
    subscriptionPoliciesReducer: SubscriptionPoliciesReducer,
    apiKeysReducer: ApiKeysReducer,
    keyType: string,
    subscriptionsReducer: SubscriptionsReducer,
    graphqlReducer: GraphqlReducer,

    onClose: () => void,
    dispatchFetchAppList: () => void,
    fetchSubscriptionPoliciesList: (organizationId: string) => Promise<void>,
    generateApiKey: (payLoad: ApiKeyGenerationRequest) => void
}

function ApiKeyGenerationWizard(props: ApiKeyGenerationWizardProps) {
    const {
        open, 
        apiInfo, 
        applicationReducer, 
        userReducer,
        subscriptionPoliciesReducer,
        apiKeysReducer,
        subscriptionsReducer,
        graphqlReducer,
        keyType,
        onClose, 
        fetchSubscriptionPoliciesList,
        dispatchFetchAppList,
        generateApiKey
    } = props;
    const classes = useStyles();
    const isInProgress = applicationReducer?.isLoading;
    const applicationList = applicationReducer?.applications;
    const generatedApiKey = apiKeysReducer?.apiKey;

    const { selectedOrg, userInfo, user} = userReducer;
    const scopes = user?.allowedScopes;

    const subscriptionPolicies = subscriptionPoliciesReducer?.subscriptionPolicies;
    const [isSubscriptionPoliciesLoaded, setIsSubscriptionPoliciesLoaded] = useState(false);
    const [isApplicationLoaded, setIsApplicationLoaded] = useState(false);
    const [isApiKeyGenerationInitiated, setIsApiKeyGenerationInitiated] = useState(false);
    
    const [isApplicationCreationInitiated, setIsApplicationCreationInitiated] = useState(false);

    const [apiKeyName, setApiKeyName] = useState("");
    const [apiKeyAssociatedApplication, setApiKeyAssociatedApplication] = useState("");
    const [selectedSubPolicy, setSelectedSubPolicy] = useState('');
    const [subscriptionApprovalRequired, setSubscriptionApprovalRequired] = useState(false);

    const [isGenerationEnabled, setIsGenerationEnabled] = useState(false);

    const [isCreateApplicationOpen, setIsCreateApplicationOpen] = useState(false);

    const [showGeneratedApiKey, setShowGeneratedApiKey] = useState(false);

    const isLoading = subscriptionPoliciesReducer?.loading || applicationReducer?.isLoading || apiKeysReducer?.isLoading;

    const { subscriptions } = subscriptionsReducer;

    const isAlreadySubscribed = (applicationId: string): boolean => {
        return subscriptions?.list.some((sub) => sub.applicationId === applicationId);
    }

    const [sanboxEnvironments, setSandboxEnvironments] = useState<EndpointInfo[]>([]);
    const [productionEnvironments, setProductionEnvironments] = useState<EndpointInfo[]>([]);

    const [endpointsNotAvailable, setEndpointsNotAvailable] = useState(false);
    const environments = graphqlReducer?.graphqlInfo?.environments;

    useEffect(() => {
        if (apiInfo?.endpointURLs) {
            if (apiInfo.endpointURLs.length > 0) {
                const sandBoxEndpoints: EndpointInfo[] = [];
                const productionEndpoints: EndpointInfo[] = [];

                const isEnvironmentAlreadyAdded = (endpoint: EndpointURL) => {
                    return sandBoxEndpoints.some((env) => env.choreoEnvironmentName === endpoint.choreoEnvironmentName) 
                        || productionEndpoints.some((env) => env.choreoEnvironmentName === endpoint.choreoEnvironmentName);
                }

                apiInfo.endpointURLs?.forEach((endpoint) => {
                    if (!endpoint.isCritical || endpoint.isSandbox) {
                        if (!isEnvironmentAlreadyAdded(endpoint)) {
                            sandBoxEndpoints.push({
                                ...endpoint,
                                url: endpoint.URLs.https,
                                environment: ""
                            });
                        }
                    }
                    if (endpoint.isCritical && !endpoint.isSandbox) {
                        if (!isEnvironmentAlreadyAdded(endpoint)) {
                            productionEndpoints.push({
                                ...endpoint,
                                url: endpoint.URLs.https,
                                environment: ""
                            });
                        }
                    }
                });
                setSandboxEnvironments(sandBoxEndpoints);
                setProductionEnvironments(productionEndpoints);
            }
        }
    }, [apiInfo]);

    const getSubscriptionPlan = (applicationId: string): string => {
        const subscription = subscriptions?.list.find((sub) => sub.applicationId === applicationId);
        return subscription?.throttlingPolicy || "";
    }

    useEffect(() => {
        if (isApplicationCreationInitiated) {
            const newApp = applicationList.list[applicationList.count - 1];
            if (newApp?.applicationId) {
                setApiKeyAssociatedApplication(newApp.applicationId);
                setIsApplicationCreationInitiated(false);
            }
        }
    }, [applicationList]);

    useEffect(() => {
        if (!isApplicationLoaded) {
            dispatchFetchAppList();
            setIsApplicationLoaded(true);
        }
    });

    useEffect(() => {
        if (!isSubscriptionPoliciesLoaded) {
            fetchSubscriptionPoliciesList(selectedOrg.uuid);
            setIsSubscriptionPoliciesLoaded(true);
        }
    });

    const validateApiKeyGenerationInputs = (): boolean => {
        return apiKeyName !== "" && apiKeyAssociatedApplication !== "" && selectedSubPolicy !== "";
    }

    useEffect(() => {
        setIsGenerationEnabled(validateApiKeyGenerationInputs());
    }, [apiKeyName, apiKeyAssociatedApplication, selectedSubPolicy]);

    const onApiKeyCreationDialogClose = () => {
        setShowGeneratedApiKey(false);
        setApiKeyAssociatedApplication("");
        setApiKeyName("");
        setSelectedSubPolicy("");
        setSubscriptionApprovalRequired(false);
        setEndpointsNotAvailable(false);
        onClose();
    }

    const [selectedEnv, setSelectedEnv] = useState("");

    useEffect(() => {
        if (isApiKeyGenerationInitiated && !apiKeysReducer.isLoading && apiKeysReducer.generateApiKeyerror != '') {
            setIsApiKeyGenerationInitiated(false);
            onClose();
        } else if(isApiKeyGenerationInitiated && !apiKeysReducer.isLoading && apiKeysReducer.generateApiKeyerror === '') {
            setShowGeneratedApiKey(true);
            setIsApiKeyGenerationInitiated(false);
        }
    }, [isApiKeyGenerationInitiated, apiKeysReducer.isLoading, apiKeysReducer.generateApiKeyerror, apiKeysReducer.apiKey]);

    const onApplicationSelection = (event: ChangeEvent<{ value: unknown }>) => {
        setApiKeyAssociatedApplication(event.target.value as string);
    }

    const validateApiKeyName = (name: string) => {
        if (name === "") {
            return "API Key name is required"; 
        }
        if (apiKeysReducer?.apiKeys?.some((key) => key.alias === name)) {
            return "API Key name already exists";
        }
        return "";
    }


    const onApiKeyNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const isValid = validateApiKeyName(event.target.value);
        if (isValid !== "") {
            setApiKeyNameError(isValid);
            setIsGenerationEnabled(false);
        } else {
            setApiKeyNameError("");
            setIsGenerationEnabled(true);
        }
        setApiKeyName(event.target.value);
    }

    const changeSubPolicy = (policyName: string) => {
        setSelectedSubPolicy(policyName);
        subscriptionPolicies.list.map((subPolicy) => {
            if (subPolicy.name === policyName) {
                if (subPolicy.attributes?.isApprovalRequired) {
                    setSubscriptionApprovalRequired(true);
                } else {
                    setSubscriptionApprovalRequired(false);
                }
            }
        });
    }

    const handleGenerateAPIKey = () => {
        setIsApiKeyGenerationInitiated(true);
        const envTemplateId = environments?.find((env) => env.name === selectedEnv)?.templateId;
        generateApiKey({
            name: apiKeyName,
            applicationId: apiKeyAssociatedApplication,
            apiId: apiInfo.id,
            subscriptionPlan: selectedSubPolicy,
            environmentTemplateId: envTemplateId || "",
            scopes: [],
            keyType: keyType
        });
    }

    const handleCreateApplication = () => {
        setIsCreateApplicationOpen(true);
        setIsApplicationCreationInitiated(true);
    }

    const [apiKeyNameError, setApiKeyNameError] = useState("");

    const NameInput = () => {
        return (
            <>
                <Box
                    justifyContent="space-between"
                    alignItems="flex-start"
                    display="flex"
                    flexDirection="row"
                >
                    <Box
                        justifyContent="space-between"
                        alignItems="flex-start"
                        display="flex"
                        flexDirection="column"
                        width={1}
                    >
                        <Typography className={classes.textLabelWithAsterisk}>
                            <FormattedMessage id='apikey.create.name' defaultMessage='Name' />
                            <sup className={classes.asterisk}> * </sup>
                        </Typography>
                        <TextField
                            onChange={onApiKeyNameChange}
                            value={apiKeyName}
                            className={classes.textInput}
                            placeholder="API Key Name"
                            variant="outlined"
                            inputProps={{
                                autoFocus: true,
                                onFocus: (e) => e.currentTarget.setSelectionRange(
                                    e.currentTarget.value.length, 
                                    e.currentTarget.value.length
                                )
                            }}
                            helperText={ apiKeyNameError !== "" && (
                                <div className={classes.helperText}>
                                    <div className={classes.warningIcon}><CustomIcons icon={'warning'} /></div>
                                    <p> {apiKeyNameError} </p>
                                </div>
                            )}
                            data-testid="app-name"
                            error={apiKeyNameError !== ""}
                        />
                    </Box>
                </Box>
            </>
        );
    };


    const ApplicationSelector = () => {
        const filteredApplications = applicationList?.list.filter((app) => {
            return ApplicationPermission.hasPermission(AppPermission.WRITE, app, userInfo, scopes);
        });
        return (
            <>
                <Box
                    justifyContent="space-between"
                    alignItems="flex-start"
                    display="flex"
                    flexDirection="row"
                >
                    <Box
                        justifyContent="space-between"
                        alignItems="flex-start"
                        display="flex"
                        flexDirection="column"
                        width={1}
                    >
                        <Typography className={classes.textLabelWithAsterisk}>
                            <FormattedMessage id='apiKey.create.associatedApplication.name' defaultMessage='Application' />
                            <sup className={classes.asterisk}> * </sup>
                        </Typography>
                        <Select
                            variant='outlined'
                            value={apiKeyAssociatedApplication}
                            onChange={onApplicationSelection}
                            className={classes.selectBox}
                            defaultValue={"Select an Application"}
                            MenuProps={{
                                getContentAnchorEl: null,
                                classes: { paper: classes.dropdownStyle },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                            }}
                            data-testid='application-selector'
                        >
                            <MenuItem value="" onClick={handleCreateApplication}>
                                <Typography style={{ color: 'blue', textDecoration: 'underline' }}>
                                    <FormattedMessage id='application.create.select' defaultMessage='+ Create an application' />
                                </Typography>
                            </MenuItem>
                            {filteredApplications?.map((app) => (
                                <MenuItem 
                                    key={app.applicationId} 
                                    value={app.applicationId} 
                                >
                                    {app.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>
            </>
        );
    };

    const SubscriptionPolicySelector = () => {
        if (apiKeyAssociatedApplication === "") {
            return null;
        }
        if (isAlreadySubscribed(apiKeyAssociatedApplication)) {
            setSelectedSubPolicy(getSubscriptionPlan(apiKeyAssociatedApplication));
            return null;
        }
        return (
            <>
                <Box
                    flexDirection={'row'}
                    alignItems={'space-between'}
                    display={'flex'}
                    my={2}
                >
                    <Typography
                    data-testid='warning-msg'
                    className={classes.textLabelWithAsterisk}
                    >
                    <FormattedMessage
                        id='apikey.create.application.subscription.required'
                        defaultMessage='Subscription Policy'
                    />
                    <sup className={classes.asterisk}> * </sup>
                    </Typography>
                </Box>
                <Grid
                    item={true}
                    xs={12}
                    md={12}
                    lg={12}
                    className={classes.policySelectorGridItem}
                >
                {apiInfo.tiers?.map((tier) => {
                return (
                    <Grid item key={"policy-" + tier.tierName} className={classes.policyCardGridItem}>
                    {subscriptionPolicies.list.map((subPolicy) => {
                        if (subPolicy.name === tier.tierName) {
                            return (
                                <SubscriptionPolicyCard
                                    subscriptionPolicy={subPolicy}
                                    handleSubPolicySelection={changeSubPolicy}
                                    key={subPolicy.name}
                                    isSelected={selectedSubPolicy === subPolicy.name}
                                />
                            );
                        }
                    })}
                    </Grid>
                );
                })}
                {subscriptionApprovalRequired && (
                    <Box
                        flexDirection={'row'}
                        alignItems={'space-between'}
                        display={'flex'}
                        my={2}
                    >
                        <Warning className={classes.warningIcon} />
                        <Typography
                        data-testid='warning-msg'
                        className={classes.warningText}
                        >
                        <FormattedMessage
                            id='apikey.create.subscription.approval.required'
                            defaultMessage='Subscription approval is required for the selected subscription policy'
                        />
                        </Typography>
                    </Box>)
                }
                </Grid>
            </>
        );
      };

    useEffect(() => {
        if (selectedEnv === "") {
            if (keyType === KeyType.SANDBOX) {
                setSelectedEnv(sanboxEnvironments[0]?.choreoEnvironmentName || '');
            }
            if (keyType === KeyType.PRODUCTION) {
                setSelectedEnv(productionEnvironments[0]?.choreoEnvironmentName || '');
            }
        }
    }, [sanboxEnvironments]);

    const setSelectedUrl = (event: ChangeEvent<{ value: unknown }>) => {
        setSelectedEnv(event.target.value as string);
    };

    const EnvironmentSelector = () => {
        if (keyType === KeyType.SANDBOX && sanboxEnvironments.length === 0) {
            setEndpointsNotAvailable(true);
            return null;
        }
        if (keyType === KeyType.PRODUCTION && productionEnvironments.length === 0) {
            setEndpointsNotAvailable(true);
            return null;
        }
        if (keyType === KeyType.SANDBOX && sanboxEnvironments.length === 1) {
            return null;
        }
        if (keyType === KeyType.PRODUCTION && productionEnvironments.length === 1) {
            return null;
        }
        return (
            <>
                <Box
                    justifyContent="space-between"
                    alignItems="flex-start"
                    display="flex"
                    flexDirection="row"
                >
                    <Box
                        justifyContent="space-between"
                        alignItems="flex-start"
                        display="flex"
                        flexDirection="column"
                        width={1}
                    >
                        <Typography className={classes.textLabelWithAsterisk}>
                            <FormattedMessage id='apiKey.create.endpoint.name' defaultMessage='Endpoint' />
                            <sup className={classes.asterisk}> * </sup>
                        </Typography>
                        <Select
                            variant='outlined'
                            value={selectedEnv}
                            onChange={setSelectedUrl}
                            className={classes.selectBox}
                            defaultValue={"Select an Endpoint"}
                            MenuProps={{
                                getContentAnchorEl: null,
                                classes: { paper: classes.dropdownStyle },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                            }}
                            data-testid='endpoint-selector'
                        >
                            {keyType === KeyType.SANDBOX && sanboxEnvironments?.map((endpoint: EndpointInfo) => (
                            <MenuItem 
                                key={endpoint.choreoEnvironmentName} 
                                value={endpoint.choreoEnvironmentName} 
                            >
                                {endpoint.choreoEnvironmentName}
                            </MenuItem>
                            ))}
                            {keyType === KeyType.PRODUCTION && productionEnvironments?.map((endpoint: EndpointInfo) => (
                                <MenuItem 
                                    key={endpoint.choreoEnvironmentName} 
                                    value={endpoint.choreoEnvironmentName}
                                >
                                    {endpoint.choreoEnvironmentName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>
            </>
            );
    };

    const ActionFooter = () => {
        return (
            <Box pb={2} mt={3} flexDirection="row" justifyContent="flex-end" display="flex">
                <GreyButton
                    disabled={isInProgress}
                    onClick={onApiKeyCreationDialogClose}
                    className={classes.cancelBtn}
                    buttonLabel={"Cancel"}
                />
                <PrimaryButton
                    onClick={handleGenerateAPIKey}
                    buttonLabel={"Generate"}
                    className={classes.dialogBtn}
                    id="create-button"
                    disabled={!isGenerationEnabled}
                />
            </Box>
        );
    };

    const LoadingLogo = () => {
        return (
            <Box
                justifyContent="center"
                alignItems="center"
                display="flex"
                height="50vh"
                width="100%"
            >
                <PreLoaderLogo size="medium" />
            </Box>
        );
    }

    const EndpointsNotAvailableWarning = () => {
        if (!(keyType === KeyType.SANDBOX && sanboxEnvironments.length === 0) &&
            !(keyType === KeyType.PRODUCTION && productionEnvironments.length === 0)) {
            return null;
        }
        setEndpointsNotAvailable(true);
        return (
            <Box
                flexDirection={'row'}
                alignItems={'space-between'}
                display={'flex'}
                my={2}
            >
                <Notification color='warning' icon='warning'>
                    <Typography variant='body2'>
                    <FormattedMessage
                        id="apiKey.create.endpoint.unavailable"
                        defaultMessage={
                        "{keyType} endpoint URL(s) are unavailable. " +
                        "Contact the API publisher to set up endpoint URLs and deploy the API."
                        }
                        values={{
                            keyType: keyType === KeyType.SANDBOX ? "Sandbox" : "Production"
                        }}
                    />
                    </Typography>
                </Notification>
            </Box>
        );
    }

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={(_,reason) => {
                    if (reason !== 'backdropClick') {
                        onApiKeyCreationDialogClose()
                    }
                }}
                aria-labelledby="delete-app-dialog-title"
                fullWidth={!isInProgress}
                classes={{ paper: classes.customWidth }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    { !isLoading &&
                        <Typography className={classes.primaryTitle}>
                            Create API Key
                        </Typography>
                    }
                    {
                        !isInProgress ? (
                            <IconButton className={classes.closeButton} onClick={onApiKeyCreationDialogClose}>
                                <CloseIcon />
                            </IconButton>
                        ) : null
                    }
                </DialogTitle>
                <DialogContent classes={{ root: classes.createApiDialogContent }}>
                    {  isLoading && <LoadingLogo />}
                    { !isLoading && isCreateApplicationOpen && 
                        <CreateApplicationFormContent 
                            onClose={() => setIsCreateApplicationOpen(false)} 
                            isEdit={false} 
                            redirectToApplication={false} 
                        />
                    }
                    { !isLoading && !isCreateApplicationOpen && showGeneratedApiKey && 
                        <Box pb={2} mt={3} flexDirection="column" justifyContent="middle" display="flex">
                            <ApiKeyView 
                                generatedApiKey={generatedApiKey} 
                                onClose={onApiKeyCreationDialogClose} 
                                isInProgress={isInProgress}
                                isSubscriptionApprovalRequired={subscriptionApprovalRequired}
                            />
                        </Box>
                    }
                    { !isLoading && !isCreateApplicationOpen && !showGeneratedApiKey && !endpointsNotAvailable &&
                        <>
                            <NameInput />
                            <EnvironmentSelector />
                            <ApplicationSelector />
                            <SubscriptionPolicySelector />
                            <ActionFooter />
                        </>
                    }
                    { !isLoading && !isCreateApplicationOpen && !showGeneratedApiKey && endpointsNotAvailable && 
                        <EndpointsNotAvailableWarning />
                    }
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        userReducer: state.userReducer,
        apiInfo: state.apiReducer.api,
        subscriptionPoliciesReducer: state.subscriptionPoliciesReducer,
        apiKeysReducer: state.apiKeysReducer,
        subscriptionsReducer: state.subscriptionsReducer,
        graphqlReducer: state.graphqlReducer
    }
};

const mapDispatchToProps = {
    dispatchFetchAppList: fetchApplicationList,
    fetchSubscriptionPoliciesList: fetchSubscriptionPoliciesList,
    generateApiKey: generateApiKeyV2,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiKeyGenerationWizard);
